/**
 * Billing history page showing invoices and receipts.
 */

import { useInfiniteQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { callable } from "../lib/callable";
import {
  FileText,
  Download,
  Loader2,
  Receipt,
} from "lucide-react";
import Header from "./Header";
import BillingTabs from "./BillingTabs";

interface BillingHistoryItem {
  id: string;
  type: "subscription" | "one_time";
  date: string;
  description: string;
  amount: number;
  currency: string;
  status: string;
  invoicePdfUrl: string | null;
  receiptUrl: string | null;
}

interface GetBillingHistoryResponse {
  items: BillingHistoryItem[];
  hasMore: boolean;
}

export default function BillingHistory() {
  const { t } = useTranslation();
  const { formatDate } = useDateFormatter();
  const getBillingHistoryFn = callable<
    { limit?: number; startingAfter?: string },
    GetBillingHistoryResponse
  >("getBillingHistory");

  // A refetch reloads every page already shown, so "Load more" never loses rows.
  const history = useInfiniteQuery({
    queryKey: ["billingHistory"],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      try {
        return (await getBillingHistoryFn({ limit: 20, startingAfter: pageParam })).data;
      } catch (err) {
        console.error("Failed to load billing history:", err);
        throw err;
      }
    },
    getNextPageParam: (last) => (last.hasMore && last.items.length > 0 ? last.items[last.items.length - 1].id : undefined),
  });
  const items = history.data?.pages.flatMap((page) => page.items) ?? [];
  const isLoading = history.isLoading;
  const isLoadingMore = history.isFetchingNextPage;
  const error = history.error ? history.error.message || t("billing.errors.loadHistory") : null;

  const handleLoadMore = () => {
    if (history.hasNextPage) history.fetchNextPage();
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const getTypeLabel = (type: string) => {
    return type === "subscription" ? t("billing.history.typeSubscription") : t("billing.history.typeBoostPurchase");
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      paid: "bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300",
      succeeded: "bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300",
      open: "bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300",
      pending: "bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300",
    };
    return styles[status] || "bg-muted text-foreground";
  };

  return (
    <div className="min-h-screen bg-muted">
      <Header />

      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-4">{t("billing.title")}</h1>

        <BillingTabs />

        <p className="text-muted-foreground mb-6">
          {t("billing.history.description")}
        </p>

        {error && (
          <div className="p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg text-red-600 mb-6">
            {error}
          </div>
        )}

        <div className="bg-card rounded-xl border">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">{t("billing.history.empty")}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {t("billing.history.emptyDescription")}
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted">
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t("billing.history.tableHeader.date")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t("billing.history.tableHeader.type")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t("billing.history.tableHeader.description")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t("billing.history.tableHeader.amount")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t("billing.history.tableHeader.status")}
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t("billing.history.tableHeader.invoiceReceipt")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {items.map((item) => (
                      <tr key={item.id} className="hover:bg-accent">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                          {formatDate(item.date, "long-date")}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                          {getTypeLabel(item.type)}
                        </td>
                        <td className="px-6 py-4 text-sm text-foreground">
                          {item.description}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground">
                          {formatAmount(item.amount, item.currency)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full capitalize ${getStatusBadge(item.status)}`}
                          >
                            {item.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          {item.invoicePdfUrl ? (
                            <a
                              href={item.invoicePdfUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-300"
                            >
                              <Download className="h-4 w-4" />
                              {t("billing.history.invoice")}
                            </a>
                          ) : item.receiptUrl ? (
                            <a
                              href={item.receiptUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-300"
                            >
                              <Receipt className="h-4 w-4" />
                              {t("billing.history.receipt")}
                            </a>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {history.hasNextPage && (
                <div className="px-6 py-4 border-t">
                  <button
                    onClick={handleLoadMore}
                    disabled={isLoadingMore}
                    className="w-full py-2 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-300 font-medium flex items-center justify-center gap-2"
                  >
                    {isLoadingMore ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {t("common.loading")}
                      </>
                    ) : (
                      t("billing.history.loadMore")
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
